import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, RefreshCw } from 'lucide-react'
import { useCustodyActions } from '../../../hooks/use-custody-actions'
import type { CustodyAction, CustodyActionType } from '../../../types/custody-actions'
import { CustodyReceiptView } from './CustodyReceiptView'
import { DestructionCertificateView } from './DestructionCertificateView'

interface CustodyActionHistoryProps {
  caseId: string
}

const ACTION_BADGE_COLOURS: Record<CustodyActionType, string> = {
  receive: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-400',
  store: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  relocate: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  return: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400',
  destroy: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
}

type ActionTitleKey =
  | 'custodyActions.receive'
  | 'custodyActions.store'
  | 'custodyActions.relocate'
  | 'custodyActions.return'
  | 'custodyActions.destroy'

const ACTION_TITLE_KEYS: Record<CustodyActionType, ActionTitleKey> = {
  receive: 'custodyActions.receive',
  store: 'custodyActions.store',
  relocate: 'custodyActions.relocate',
  return: 'custodyActions.return',
  destroy: 'custodyActions.destroy',
}

const PER_PAGE = 10

interface ActionRowProps {
  action: CustodyAction
  onViewDocument: (action: CustodyAction) => void
}

function ActionRow({ action, onViewDocument }: ActionRowProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  const badgeClass = ACTION_BADGE_COLOURS[action.actionType]
  const titleKey = ACTION_TITLE_KEYS[action.actionType]

  return (
    <tr className="border-b border-[var(--border)] transition-colors hover:bg-[var(--bg-elevated)]">
      <td className="px-4 py-3">
        <span
          className={`rounded px-2 py-0.5 text-xs font-[var(--font-mono)] uppercase ${badgeClass}`}
        >
          {t(titleKey)}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-[var(--text-primary)]">{action.actionDate}</td>
      <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
        {action.performedByName ?? action.performedBy}
      </td>
      <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
        {action.evidenceNumberIds.length}
      </td>
      <td className="px-4 py-3">
        {action.documentId !== null && (
          <button
            type="button"
            onClick={() => {
              onViewDocument(action)
            }}
            className="flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
          >
            <FileText className="h-3 w-3" />
            {t('custodyActions.viewDocument')}
          </button>
        )}
      </td>
    </tr>
  )
}

interface PaginationProps {
  page: number
  total: number
  onPageChange: (page: number) => void
}

function Pagination({ page, total, onPageChange }: PaginationProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  const totalPages = Math.ceil(total / PER_PAGE)

  return (
    <div className="flex items-center justify-between px-4 py-2 text-xs text-[var(--text-muted)]">
      <span>
        {t('custodyActions.page')} {page} {t('custodyActions.of')} {totalPages}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1)
          }}
          className="rounded border border-[var(--border)] px-2 py-1 disabled:opacity-50"
        >
          &laquo;
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => {
            onPageChange(page + 1)
          }}
          className="rounded border border-[var(--border)] px-2 py-1 disabled:opacity-50"
        >
          &raquo;
        </button>
      </div>
    </div>
  )
}

interface HistoryTableProps {
  actions: CustodyAction[]
  total: number
  page: number
  onViewDocument: (action: CustodyAction) => void
  onPageChange: (page: number) => void
}

function HistoryTable({
  actions,
  total,
  page,
  onViewDocument,
  onPageChange,
}: HistoryTableProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-[var(--border)] text-xs text-[var(--text-muted)] uppercase">
            <tr>
              <th className="px-4 py-3">{t('custodyActions.actionType')}</th>
              <th className="px-4 py-3">{t('custodyActions.date')}</th>
              <th className="px-4 py-3">{t('custodyActions.performer')}</th>
              <th className="px-4 py-3">{t('custodyActions.evidenceCount')}</th>
              <th className="px-4 py-3">{t('custodyActions.document')}</th>
            </tr>
          </thead>
          <tbody>
            {actions.map((action) => (
              <ActionRow key={action.id} action={action} onViewDocument={onViewDocument} />
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} total={total} onPageChange={onPageChange} />
    </>
  )
}

interface DocumentViewerProps {
  caseId: string
  action: CustodyAction
  onClose: () => void
}

function DocumentViewer({ caseId, action, onClose }: DocumentViewerProps): React.ReactElement {
  if (action.actionType === 'destroy') {
    return (
      <DestructionCertificateView
        caseId={caseId}
        actionId={action.id}
        open={true}
        onClose={onClose}
      />
    )
  }
  return <CustodyReceiptView caseId={caseId} actionId={action.id} open={true} onClose={onClose} />
}

export function CustodyActionHistory({ caseId }: CustodyActionHistoryProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  const [page, setPage] = useState(1)
  const [viewingAction, setViewingAction] = useState<CustodyAction | null>(null)
  const { actions, total, loading, error } = useCustodyActions(caseId, { page, perPage: PER_PAGE })

  const handleViewDocument = useCallback((action: CustodyAction) => {
    setViewingAction(action)
  }, [])
  const handleCloseDocument = useCallback(() => {
    setViewingAction(null)
  }, [])

  return (
    <div className="space-y-2">
      <h3 className="text-base font-[var(--font-heading)] font-semibold text-[var(--text-primary)]">
        {t('custodyActions.history')}
      </h3>
      <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-card)]">
        {loading && (
          <div className="flex h-32 items-center justify-center" role="status" aria-busy="true">
            <RefreshCw className="h-5 w-5 animate-spin text-[var(--accent)]" />
          </div>
        )}
        {error && (
          <div className="p-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </div>
        )}
        {!loading && !error && actions.length === 0 && (
          <p className="p-4 text-sm text-[var(--text-muted)]">{t('custodyActions.noActions')}</p>
        )}
        {!loading && !error && actions.length > 0 && (
          <HistoryTable
            actions={actions}
            total={total}
            page={page}
            onViewDocument={handleViewDocument}
            onPageChange={setPage}
          />
        )}
      </div>
      {viewingAction !== null && (
        <DocumentViewer caseId={caseId} action={viewingAction} onClose={handleCloseDocument} />
      )}
    </div>
  )
}
